import DecoupledEditor from "@ckeditor/ckeditor5-build-decoupled-document/build/ckeditor";
import { Editor } from "./Editor";
import AddSlide from "./slide";

export interface SlideMap {
	slideContent: string;
	startingIndex: number;
	endingIndex: number;
}

export interface CkEditorSelection {
	attributes?: { addSlide?: boolean };
	children?: CkEditorSelection[];
	data: string;
}

export const CkEditorRegex = {
	noSlides: new RegExp(/<(slide|\/slide)[^>]{0,}>/g),
	noMarks: new RegExp(/<(strong|\/strong|span|\/span|em|\/em|i|\/i|u|\/u|mark|\/mark)[^>]{0,}>/g),
	noBlocks: new RegExp(/<(blockquote|\/blockquote|p|\/p|h1|\/h1|h2|\/h2|h3|\/h3|h4|\/h4|ul|\/ul|ol|\/ol|li|\/li)[^>]{0,}>/g),
	siblingSlides: new RegExp(/<\/slide><slide>/g),
	softBreak: new RegExp(/<br>/g),
};

export const CkEditorHelper = {
	getSlides: (data: string) => {
		let slideMap = [];

		let slideStartIndexes = getIndexesOfAllOccurrences(data, /<slide/);
		let slideEndIndexes = getIndexesOfAllOccurrences(data, /<\/slide>/);

		// in theory, normalized html should contain an equal number of opening and closing tags so the arrays should be equal length
		for (let i = 0; i < slideStartIndexes.length; i++) {
			const startingIndex = slideStartIndexes[i];
			const endingIndex = slideEndIndexes[i] + "</slide>".length;

			const contentStartingIndex = startingIndex + "<slide>".length;
			const contentEndingIndex = slideEndIndexes[i] - contentStartingIndex;

			const innerHTML = data.substr(contentStartingIndex, contentEndingIndex);

			const slideContent = innerHTML.replace(CkEditorRegex.noMarks, "");

			const mapping = {
				startingIndex,
				endingIndex,
				slideContent,
			};

			slideMap.push(mapping);
		}

		return SlideProccessor.combineSiblingSlides(slideMap, data);
	},

	removeSlide(data: string, slide: SlideMap) {
		return (
			data.substring(0, slide.startingIndex) +
			data.substring(slide.startingIndex, slide.endingIndex).replace(CkEditorRegex.noSlides, "") +
			data.substring(slide.endingIndex)
		);
	},

	removePreviewSlide(slidesToBeRemovedIds: string[], editorRef: Editor) {
		let slideElements = slidesToBeRemovedIds.map((id) => {
			return document.getElementById(id);
		});

		slideElements.forEach((slideElementToBeRemoved) => {
			if (slideElementToBeRemoved === null) {
				return;
			}

			//insert all child nodes of the element to be removed before itself
			while (slideElementToBeRemoved.firstChild !== null) {
				slideElementToBeRemoved.parentNode?.insertBefore(slideElementToBeRemoved.firstChild, slideElementToBeRemoved);
			}
			slideElementToBeRemoved.parentNode?.removeChild(slideElementToBeRemoved);
		});

		let newEditorData = document.getElementById("editor")?.innerHTML.toString();
		editorRef.setData(newEditorData!);
	},

	getSelections(editor: any) {
		let content = editor.model.getSelectedContent(editor.model.document.selection);
		let selections: CkEditorSelection[] = content.toJSON();
		return selections;
	},

	isAttributeActionActive: (selections: CkEditorSelection[], action: string) => {
		let firstElement = selections[0];
		let firstElementIsAction = firstElement.attributes != null && (firstElement.attributes as any)[action] != null;

		return firstElementIsAction;
	},

	createEditorInDOM: async (editorElement: Element) => {
		DecoupledEditor.builtinPlugins = [...DecoupledEditor.builtinPlugins, AddSlide];
		const editor: Editor = await DecoupledEditor.create(editorElement, CkEditorHelper.config);

		const toolbarContainer = document.querySelector("#toolbar-container");
		if (toolbarContainer) {
			toolbarContainer.appendChild(editor.ui.view.toolbar.element);
		}

		return editor;
	},

	injectCopyPasteButtonsIntoToolbar: (onCopyButtonClick: () => void, onPasteButtonClick: () => void) => {
		const toolbarCollection = document.getElementsByClassName("ck ck-toolbar ck-reset_all ck-rounded-corners");
		let toolbarElement = toolbarCollection.item(0);

		if (toolbarElement) {
			const copyPasteElement = document.createElement("span");

			const copyButton = document.createElement("button");
			copyButton.className = "ck ck-button ck-off";
			copyButton.id = "copyButton";
			copyButton.innerHTML =
				'<span class="copyIcon"></span><span class="ck ck-tooltip ck-tooltip_s"><span class="ck ck-tooltip__text">Copy (CTRL + C)</span></span><span class="ck ck-button__label" id="ck-editor__aria-label_e9a8dfdd1b51abd04b4a796f3f5e5427a">Copy</span>';
			copyButton.onclick = onCopyButtonClick;

			const pasteButton = document.createElement("button");
			pasteButton.className = "ck ck-button ck-off";
			pasteButton.id = "pasteButton";
			pasteButton.innerHTML =
				'<span class="pasteIcon"></span><span class="ck ck-tooltip ck-tooltip_s"><span class="ck ck-tooltip__text">Paste (CTRL + V)</span></span><span class="ck ck-button__label" id="ck-editor__aria-label_e9a8dfdd1b51abd04b4a796f3f5e5427a">Paste</span>';
			pasteButton.onclick = onPasteButtonClick;

			const verticalDivider = document.createElement("span");
			verticalDivider.className = "ck ck-toolbar__separator";

			copyPasteElement.appendChild(copyButton);
			copyPasteElement.appendChild(pasteButton);

			toolbarElement.insertBefore(copyPasteElement, toolbarElement.childNodes[3]);
			toolbarElement.insertBefore(verticalDivider, toolbarElement.childNodes[4]);
		}
	},

	config: {
		toolbar: [
			"undo",
			"redo",
			"|",
			"heading",
			"fontFamily",
			"fontSize",
			"|",
			"bold",
			"italic",
			"underline",
			"highlight",
			"|",
			"alignment",
			"|",
			"bulletedList",
			"numberedList",
			"blockquote",
		],
		heading: {
			options: [
				{ model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
				{ model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
				{ model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
			],
		},
		fontFamily: {
			options: ["default", "Ubuntu, Arial, sans-serif", "Helvetica Neue", "Roboto"],
		},
		fontSize: {
			options: [9, 11, 13, "default", 17, 19, 21],
		},
		highlight: {
			options: [
				{ model: "yellowMarker", class: "marker-yellow", title: "Yellow Marker", color: "#fdfd77", type: "marker" },
				{ model: "greenMarker", class: "marker-green", title: "Green marker", color: "#63f963", type: "marker" },
				{ model: "lightRedMarker", class: "marker-light-red", title: "Light Red marker", color: "#fc7999", type: "marker" },
				{ model: "blueMarker", class: "marker-blue", title: "Blue marker", color: "#72cdfd", type: "marker" },
				{ model: "orangeMarker", class: "marker-orange", title: "Orange marker", color: "#ff9800", type: "marker" },
				{ model: "purpleMarker", class: "marker-purple", title: "Purple marker", color: "#ce93d8", type: "marker" },
				{ model: "blackMarker", class: "marker-black", title: "Black marker", color: "#000", type: "marker" },
				{ model: "redPen", class: "pen-red", title: "Red pen", color: "#e91313", type: "pen" },
				{ model: "bluePen", class: "pen-blue", title: "Blue pen", color: "#72cdfd", type: "pen" },
			],
		},
	},
};

const SlideProccessor = {
	combineSiblingSlides(slideMapping: SlideMap[], data: string) {
		if (slideMapping.length < 2) {
			return slideMapping;
		}
		let combinedSlideMapping: SlideMap[] = [];

		let previousSlide: SlideMap = slideMapping[0];
		for (let i = 1; i < slideMapping.length; i++) {
			const combinedSlide = SlideProccessor.combineSlides(previousSlide, slideMapping[i], data);

			// Slides are not siblings
			if (combinedSlide === null) {
				combinedSlideMapping.push(previousSlide);
				previousSlide = slideMapping[i];
			} else {
				previousSlide = combinedSlide;
			}
		}

		combinedSlideMapping.push(previousSlide);

		return combinedSlideMapping;
	},

	combineSlides(slide1: SlideMap, slide2: SlideMap, data: string) {
		let combinedSlideHtml = data.substring(slide1.startingIndex, slide2.endingIndex);
		let dataWithoutMarks = combinedSlideHtml.replace(CkEditorRegex.noMarks, "");
		let dataWithoutBlocks = dataWithoutMarks.replace(CkEditorRegex.noBlocks, "");

		if (getIndexesOfAllOccurrences(dataWithoutBlocks, /<\/slide>/).length > 1) {
			return null;
		}

		let combinedSlide: SlideMap = {
			slideContent: dataWithoutBlocks.replace(CkEditorRegex.noSlides, ""),
			startingIndex: slide1.startingIndex,
			endingIndex: slide2.endingIndex,
		};

		return combinedSlide;
	},
};

function getIndexesOfAllOccurrences(str: string, toMatch: RegExp) {
	let re = new RegExp(toMatch, "g"),
		indexMatches = [],
		match;

	while ((match = re.exec(str)) !== null) {
		indexMatches.push(match.index);
	}

	return indexMatches;
}
